import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Slider from "@material-ui/core/Slider";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
}));

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = (key, fallback = "") => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : fallback;
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="ignoreMessagesAfterClose-setting"
						label="Após fechar ticket, não reabrir por (minutos, 0 = desativado) — mensagens ficam gravadas no ticket fechado"
						margin="dense"
						variant="outlined"
						fullWidth
						type="number"
						inputProps={{ min: 0 }}
						value={settings && settings.length > 0 ? getSettingValue("ignoreMessagesAfterClose", "0") : "0"}
						onChange={async e => {
							await handleChangeSetting({
								target: { value: e.target.value, name: "ignoreMessagesAfterClose" }
							});
						}}
					/>
				</Paper>

				<Typography variant="body2" gutterBottom style={{ marginTop: 16, marginBottom: 4 }}>
					Alertas de tempo de espera nos tickets
				</Typography>

				<Paper className={classes.paper} style={{ borderLeft: "4px solid #f6c90e" }}>
					<TextField
						id="alertTimeLevel1-setting"
						label="Alerta amarelo — aguardando há mais de (minutos)"
						margin="dense"
						variant="outlined"
						fullWidth
						type="number"
						inputProps={{ min: 1 }}
						value={settings && settings.length > 0 ? getSettingValue("alertTimeLevel1", "15") : "15"}
						onChange={async e => {
							await handleChangeSetting({
								target: { value: e.target.value, name: "alertTimeLevel1" }
							});
						}}
					/>
				</Paper>

				<Paper className={classes.paper} style={{ borderLeft: "4px solid #ff9800" }}>
					<TextField
						id="alertTimeLevel2-setting"
						label="Alerta laranja — aguardando há mais de (minutos)"
						margin="dense"
						variant="outlined"
						fullWidth
						type="number"
						inputProps={{ min: 1 }}
						value={settings && settings.length > 0 ? getSettingValue("alertTimeLevel2", "30") : "30"}
						onChange={async e => {
							await handleChangeSetting({
								target: { value: e.target.value, name: "alertTimeLevel2" }
							});
						}}
					/>
				</Paper>

				<Paper className={classes.paper} style={{ borderLeft: "4px solid #f44336" }}>
					<TextField
						id="alertTimeLevel3-setting"
						label="Alerta vermelho — aguardando há mais de (minutos)"
						margin="dense"
						variant="outlined"
						fullWidth
						type="number"
						inputProps={{ min: 1 }}
						value={settings && settings.length > 0 ? getSettingValue("alertTimeLevel3", "60") : "60"}
						onChange={async e => {
							await handleChangeSetting({
								target: { value: e.target.value, name: "alertTimeLevel3" }
							});
						}}
					/>
				</Paper>

				<Typography variant="body2" gutterBottom style={{ marginTop: 16, marginBottom: 4 }}>
					Mensagem de boas-vindas ao atender
				</Typography>

				<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
					<FormControlLabel
						control={
							<Switch
								checked={getSettingValue("greetingOnAcceptEnabled", "false") === "true"}
								onChange={async e => {
									await handleChangeSetting({
										target: { value: e.target.checked ? "true" : "false", name: "greetingOnAcceptEnabled" }
									});
								}}
								color="primary"
							/>
						}
						label="Enviar mensagem automática ao clicar em Atender"
					/>

					<TextField
						label="Mensagem de boas-vindas"
						helperText="Use {{name}} para o nome do contato, {{atendente}} para o nome de quem está atendendo e {{ticket}} para o número do ticket. Shift+clique em Atender pula o envio."
						multiline
						rows={3}
						variant="outlined"
						fullWidth
						value={getSettingValue("greetingOnAcceptMessage", "")}
						onChange={async e => {
							await handleChangeSetting({ target: { value: e.target.value, name: "greetingOnAcceptMessage" } });
						}}
					/>
				</Paper>

				<Typography variant="body2" gutterBottom style={{ marginTop: 16, marginBottom: 4 }}>
					Mensagem de transferência de atendimento
				</Typography>

				<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
					<FormControlLabel
						control={
							<Switch
								checked={getSettingValue("transferMessageEnabled", "false") === "true"}
								onChange={async e => {
									await handleChangeSetting({
										target: { value: e.target.checked ? "true" : "false", name: "transferMessageEnabled" }
									});
								}}
								color="primary"
							/>
						}
						label="Enviar mensagem automática ao transferir o chamado para outro atendente"
					/>

					<TextField
						label="Mensagem de transferência"
						helperText="Use {{name}} para o nome do contato e {{atendente}} para o nome de quem recebeu o chamado. Só dispara quando o chamado já tinha um atendente e é reatribuído a outro."
						multiline
						rows={3}
						variant="outlined"
						fullWidth
						value={getSettingValue("transferMessageText", "")}
						onChange={async e => {
							await handleChangeSetting({ target: { value: e.target.value, name: "transferMessageText" } });
						}}
					/>
				</Paper>

				<Typography variant="body2" gutterBottom style={{ marginTop: 16, marginBottom: 4 }}>
					Pesquisa de satisfação (1 a 5)
				</Typography>

				<Paper className={classes.paper} style={{ flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
					<FormControlLabel
						control={
							<Switch
								checked={getSettingValue("surveyEnabled", "false") === "true"}
								onChange={async e => {
									await handleChangeSetting({
										target: { value: e.target.checked ? "true" : "false", name: "surveyEnabled" }
									});
								}}
								color="primary"
							/>
						}
						label="Enviar pesquisa ao fechar ticket (conexão padrão)"
					/>

					<div style={{ width: "100%", paddingRight: 16 }}>
						<Typography variant="body2" gutterBottom>
							Aguardar antes de enviar: {getSettingValue("surveyDelay", "0")} min
						</Typography>
						<Slider
							min={0}
							max={2}
							step={1}
							marks={[{ value: 0, label: "0" }, { value: 1, label: "1 min" }, { value: 2, label: "2 min" }]}
							value={parseInt(getSettingValue("surveyDelay", "0"), 10)}
							onChange={async (_, val) => {
								await handleChangeSetting({ target: { value: String(val), name: "surveyDelay" } });
							}}
						/>
					</div>

					<TextField
						label="Mensagem de pesquisa"
						multiline
						rows={3}
						variant="outlined"
						fullWidth
						value={getSettingValue("surveyMessage", "")}
						onChange={async e => {
							await handleChangeSetting({ target: { value: e.target.value, name: "surveyMessage" } });
						}}
					/>

					<TextField
						label="Gerar alerta quando nota ≤"
						type="number"
						variant="outlined"
						inputProps={{ min: 1, max: 5 }}
						style={{ width: 180 }}
						value={getSettingValue("ratingAlertThreshold", "2")}
						onChange={async e => {
							await handleChangeSetting({ target: { value: e.target.value, name: "ratingAlertThreshold" } });
						}}
					/>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
